'use client';

import { Box } from '@terra/ui-styled-system';
import { BodyDefault, Skeleton } from '../../';
import { useState, type ReactNode } from 'react';
import { css } from '@terra/ui-styled-system';

const imageStyles = css({
  transition: 'opacity 0.3s ease-in-out',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: 'inherit',
});

interface ImageWithSkeletonProps {
  src?: string | null;
  alt: string;
  errorMessage?: string;
  fallback?: ReactNode;
  objectFit?: 'cover' | 'contain';
}

export function ImageWithSkeleton({
  src,
  alt,
  objectFit,
  errorMessage = 'Failed to load image',
  fallback,
}: ImageWithSkeletonProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const shouldShowFallback = !src || hasError;

  return (
    <Box position="relative" w="full" h="full">
      {!isLoaded && !shouldShowFallback && (
        <Skeleton position="absolute" inset={0} borderRadius="md" />
      )}
      {!shouldShowFallback && (
        <img
          loading="lazy"
          src={src}
          alt={alt}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={imageStyles}
          style={{
            opacity: isLoaded ? 1 : 0,
            ...(objectFit && { objectFit }),
          }}
        />
      )}
      {shouldShowFallback && (
        <Box
          position="absolute"
          inset={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="bg.neutral.subtle"
          borderRadius="md"
        >
          {fallback ?? (
            <BodyDefault color="fg.muted">{errorMessage}</BodyDefault>
          )}
        </Box>
      )}
    </Box>
  );
}
